/**
 * Everything the organization has reviewed, grouped by counterparty — the
 * fund, client or provider on the other side — with each document's open
 * gaps and the review it belongs to. Groups fold like Governance's lists.
 *
 * @module components/apps/contractIntelligence/CiDocumentsPage
 */
import { Link } from "@tanstack/react-router";
import { Building2Icon, ChevronRightIcon, FileTextIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "../../../lib/utils";
import { useCiDocuments, type CiDocumentListing } from "../../../state/contractIntelligence";
import { SeverityGlyph } from "../../regulation/finding/findingGlyphs";
import { groupByCounterparty, shortDate } from "./ciModel";
import { CiEmpty, CiListSkeleton, CiPage, CiSearch, CiUnavailable, ReviewStatusMark } from "./ciParts";
import { useCiOrg } from "./useCiOrg";

function DocumentRow({ document, orgSegment }: { readonly document: CiDocumentListing; readonly orgSegment: string }) {
  return (
    <Link
      to="/$org/apps/contract-intelligence/reviews/$reviewId"
      params={{ org: orgSegment, reviewId: document.reviewId }}
      className="flex h-11 min-w-0 items-center gap-3 ps-11 pe-4 text-[13px] outline-none transition-colors hover:bg-accent/40 focus-visible:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset sm:pe-6"
      data-ci-document-row={document.id}
    >
      <FileTextIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">{document.name}</span>
      {document.documentType ? (
        <span className="hidden h-5 shrink-0 items-center rounded-md border border-border/70 px-1.5 text-[11px] text-muted-foreground sm:inline-flex">
          {document.documentType}
        </span>
      ) : null}
      <span className="hidden min-w-0 max-w-56 items-center gap-1.5 truncate text-xs text-muted-foreground md:inline-flex">
        <ReviewStatusMark status={document.reviewStatus} className="scale-90" />
        <span className="truncate">{document.reviewTitle}</span>
      </span>
      <span className="inline-flex w-16 shrink-0 items-center justify-end gap-1 text-xs tabular-nums text-muted-foreground">
        {document.openGaps > 0 ? (
          <>
            <SeverityGlyph severity={document.highestOpenSeverity} className="size-3.5" />
            {document.openGaps}
          </>
        ) : (
          <span className="text-muted-foreground/70">—</span>
        )}
      </span>
      <time className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground" dateTime={document.createdAt}>
        {shortDate(document.createdAt)}
      </time>
    </Link>
  );
}

export function CiDocumentsPage() {
  const { organizationId, orgSegment } = useCiOrg();
  const documents = useCiDocuments(organizationId);
  const [query, setQuery] = useState("");
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const list = documents.read?.kind === "ready" ? documents.read.value : null;
  const groups = useMemo(() => (list ? groupByCounterparty(list, query) : []), [list, query]);
  const toggle = (key: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <CiPage
      title="Documents"
      meta={list ? list.length : undefined}
      data-ci-page="documents"
      actions={list && list.length > 0 ? <CiSearch value={query} onChange={setQuery} label="Search documents" placeholder="Search documents" className="hidden sm:flex" /> : null}
    >
      {documents.read === null ? (
        <CiListSkeleton />
      ) : documents.read.kind !== "ready" ? (
        <CiUnavailable what="Documents" onRetry={documents.reload} />
      ) : list && list.length === 0 ? (
        <CiEmpty
          title="Nothing reviewed yet"
          description="Every document you review appears here, grouped by the counterparty it is with."
        />
      ) : groups.length === 0 ? (
        <p className="px-6 py-12 text-center text-sm text-muted-foreground">No documents match.</p>
      ) : orgSegment ? (
        <div>
          <div className="border-b border-border/50 px-4 py-2 sm:hidden">
            <CiSearch value={query} onChange={setQuery} label="Search documents" placeholder="Search documents" />
          </div>
          {groups.map((group) => {
            const open = !folded.has(group.key);
            return (
              <section key={group.key} aria-label={group.counterparty ?? "No counterparty"}>
                <button
                  type="button"
                  onClick={() => toggle(group.key)}
                  aria-expanded={open}
                  className="flex h-10 w-full items-center gap-2 border-b border-border/50 bg-muted/25 px-4 text-left text-[13px] outline-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset sm:px-6"
                >
                  <ChevronRightIcon aria-hidden className={cn("size-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")} />
                  <Building2Icon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className={cn("truncate font-medium", group.counterparty === null && "text-muted-foreground")}>
                    {group.counterparty ?? "No counterparty named"}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">{group.documents.length}</span>
                  <span className="flex-1" />
                  {group.openGaps > 0 ? (
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {group.openGaps} open gap{group.openGaps === 1 ? "" : "s"}
                    </span>
                  ) : null}
                </button>
                {open ? (
                  <div className="divide-y divide-border/40 border-b border-border/50">
                    {group.documents.map((document) => (
                      <DocumentRow key={document.id} document={document} orgSegment={orgSegment} />
                    ))}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      ) : null}
    </CiPage>
  );
}
